import type {
  BuildingGraph,
  BuildingGraphFaceNode,
  BuildingGraphPlanarTopology,
  BuildingGraphPlane,
  BuildingGraphVolumeEdge,
  BuildingGraphVolumeNode,
  BuildingGraphVolumeParentHandle,
} from "building-types/building/buildingGraphTypes";
import type { BuildingGraphFilterBuilding } from "building-types/buildingGraphFilter/buildingGraphFilterBuilding";
import type { PlanarTopologyFaceRow } from "building-types/buildingGraphFilter/entitySource";
import type { WallPlaneSideKind } from "building-types/buildingGraphFilter/propertyCatalog";
import { getSourceRoomId } from "building-types/components/componentInstanceUtils";
import type { FreeformWallEdgeHandle, RoomEdgeHandle } from "building-types/handles/docEntityHandleTypes";
import type { RoomClass, RoomEnclosure, RoomType } from "building-types/rooms/roomTypesEnum";
import type { RoomAttributes } from "building-types/rooms/staticRoomAttributes";
import { WindingDir } from "geom/core";
import { planeCanonicalFromRefPlane, planeHash } from "geom/plane";
import {
  faceNodeFreeformWallParentHandle,
  faceNodeGetFace,
  faceNodeGetVolumeNode,
  faceNodeGetVolumeNodeFaceIndex,
} from "../buildingGraph/faceNodeParent";
import { staticRoomAttributes } from "../elements/roomAttributes";

export function volumeNodeParentHandleGetRoomId(
  parentHandle: BuildingGraphVolumeParentHandle | undefined,
): string | undefined {
  if (parentHandle === undefined || parentHandle.type !== "ROOM") {
    return undefined;
  }
  return parentHandle.roomId;
}

export function volumeNodeParentHandleGetRoofId(
  parentHandle: BuildingGraphVolumeParentHandle | undefined,
): string | undefined {
  if (parentHandle === undefined || parentHandle.type !== "ROOF") {
    return undefined;
  }
  return parentHandle.roofId;
}

export function volumeNodeParentHandleGetFloorId(
  parentHandle: BuildingGraphVolumeParentHandle | undefined,
): string | undefined {
  if (parentHandle === undefined || parentHandle.type !== "FLOOR") {
    return undefined;
  }
  return parentHandle.floorId;
}

/**
 * Parent handles that contribute membership for a volume node.
 * Direct ROOM/FLOOR/ROOF parents return themselves; VOLUME_EDGE parents expand to the
 * endpoint volume parents (e.g. ROOM∩FLOOR → both ROOM and FLOOR).
 */
export function volumeNodeGetMembershipParentHandles(
  volumeNode: BuildingGraphVolumeNode,
  buildingGraph: BuildingGraphFilterBuilding["buildingGraph"],
): readonly BuildingGraphVolumeParentHandle[] {
  const parentHandle = volumeNode.parentHandle;
  if (parentHandle === undefined) {
    return [];
  }
  if (parentHandle.type !== "VOLUME_EDGE") {
    return [parentHandle];
  }

  const volumeEdge = buildingGraph.volumeEdges[parentHandle.volumeEdgeIndex];
  if (!volumeEdge) {
    return [parentHandle];
  }

  const membershipHandles: BuildingGraphVolumeParentHandle[] = [];
  const endpointA = buildingGraph.volumeNodes[volumeEdge.volumeANodeId]?.parentHandle;
  const endpointB = buildingGraph.volumeNodes[volumeEdge.volumeBNodeId]?.parentHandle;
  if (endpointA) {
    membershipHandles.push(endpointA);
  }
  if (endpointB) {
    membershipHandles.push(endpointB);
  }
  return membershipHandles;
}

export function volumeNodeParentGetRoomAttributes(
  parentHandle: BuildingGraphVolumeParentHandle | undefined,
  building: BuildingGraphFilterBuilding,
): RoomAttributes | undefined {
  if (parentHandle === undefined || parentHandle.type !== "ROOM") {
    return undefined;
  }
  return roomGetRoomAttributes(parentHandle.roomId, building);
}

export function volumeNodeParentHandleGetRoomClass(
  parentHandle: BuildingGraphVolumeParentHandle | undefined,
  building: BuildingGraphFilterBuilding,
): RoomClass | undefined {
  return volumeNodeParentGetRoomAttributes(parentHandle, building)?.roomClass;
}

export function volumeNodeParentHandleGetRoomEnclosure(
  parentHandle: BuildingGraphVolumeParentHandle | undefined,
  building: BuildingGraphFilterBuilding,
): RoomEnclosure | undefined {
  return volumeNodeParentGetRoomAttributes(parentHandle, building)?.roomEnclosure;
}

export function volumeNodeGetPlaneHashes(
  volumeNode: BuildingGraphVolumeNode,
  building: BuildingGraphFilterBuilding,
): ReadonlySet<string> {
  const planeHashes = new Set<string>();
  for (const faceNodeId of volumeNode.faceNodeIds) {
    const faceNode = building.buildingGraph.faceNodes[faceNodeId];
    if (!faceNode) {
      continue;
    }
    const planeHash = faceNodeGetPlaneHash(faceNode, building);
    if (planeHash) {
      planeHashes.add(planeHash);
    }
  }
  return planeHashes;
}

// TODO - delete and replace usage with volumeNodeGetPlaneHashes
// this function is only used in the volumeEdgeSource, but is left here because i'm lazy
export function volumeNodeIdGetPlaneHashes(
  volumeNodeId: string,
  building: BuildingGraphFilterBuilding,
): ReadonlySet<string> {
  const planeHashes = new Set<string>();
  const volumeNode = building.buildingGraph.volumeNodes[volumeNodeId];
  if (!volumeNode) {
    return planeHashes;
  }
  return volumeNodeGetPlaneHashes(volumeNode, building);
}

export function volumeEdgeGetPlaneHashes(
  volumeEdge: BuildingGraphVolumeEdge,
  building: BuildingGraphFilterBuilding,
): ReadonlySet<string> {
  const planeHashes = new Set<string>();
  for (const planeHash of volumeNodeIdGetPlaneHashes(volumeEdge.volumeANodeId, building)) {
    planeHashes.add(planeHash);
  }
  for (const planeHash of volumeNodeIdGetPlaneHashes(volumeEdge.volumeBNodeId, building)) {
    planeHashes.add(planeHash);
  }
  return planeHashes;
}

/**
 * Volume entity parent of a face node (room / floor / roof / volume edge). Face nodes
 * without a volume parent (e.g. freeform walls) have no volume entity parent - use
 * {@link faceNodeFreeformWallParentHandle} for those.
 */
export function faceNodeGetParentHandle(
  faceNode: BuildingGraphFaceNode,
  buildingGraph: BuildingGraphFilterBuilding["buildingGraph"],
): BuildingGraphVolumeParentHandle | undefined {
  return faceNodeGetVolumeNode(faceNode, buildingGraph)?.parentHandle;
}

export function faceNodeGetBuildingGraphPlane(
  faceNode: BuildingGraphFaceNode,
  buildingGraph: BuildingGraph,
): BuildingGraphPlane | null {
  const face = faceNodeGetFace(faceNode, buildingGraph);
  if (!face) {
    return null;
  }
  const refPlane = planeCanonicalFromRefPlane(face.plane);
  if (!refPlane) {
    return null;
  }
  return buildingGraph.buildingPlanes?.[planeHash(refPlane)] ?? null;
}

export function faceNodeGetPlaneHash(
  faceNode: BuildingGraphFaceNode,
  building: BuildingGraphFilterBuilding,
): string | undefined {
  return faceNodeGetBuildingGraphPlane(faceNode, building.buildingGraph)?.planeHash;
}

export function faceNodeGetWallPlaneKinds(
  faceNode: BuildingGraphFaceNode,
  building: BuildingGraphFilterBuilding,
): ReadonlySet<WallPlaneSideKind> {
  const kinds = new Set<WallPlaneSideKind>();
  const volumeNode = faceNodeGetVolumeNode(faceNode, building.buildingGraph);
  if (volumeNode) {
    for (const parentHandle of volumeNodeGetMembershipParentHandles(volumeNode, building.buildingGraph)) {
      if (parentHandle.type === "ROOM") {
        kinds.add("ROOM_FACE");
      }
      if (parentHandle.type === "ROOF") {
        kinds.add("ROOF_FACE");
      }
      if (parentHandle.type === "FLOOR") {
        kinds.add("FLOOR_FACE");
      }
    }
  }
  if (faceNode.parentHandle.type === "FREEFORM_WALL") {
    kinds.add("FREEFORM_WALL_FACE");
  }
  const incident = building.buildingGraph.faceNodesToEdgeIds?.[faceNode.faceNodeId];
  if (incident === undefined || incident.length === 0) {
    kinds.add("EXTERIOR");
  }

  return kinds;
}

export function faceNodeGetRoomId(
  faceNode: BuildingGraphFaceNode,
  building: BuildingGraphFilterBuilding,
): string | undefined {
  const volumeNode = faceNodeGetVolumeNode(faceNode, building.buildingGraph);
  if (!volumeNode) {
    return undefined;
  }
  return volumeNodeGetRoomId(volumeNode, building);
}

export function faceNodeGetRoomEdgeHandle(
  faceNode: BuildingGraphFaceNode,
  building: BuildingGraphFilterBuilding,
): RoomEdgeHandle | undefined {
  const volumeNode = faceNodeGetVolumeNode(faceNode, building.buildingGraph);
  // Intersection BRep face indices are not room wall order; volumeNodeFaceIndex - 2 would
  // invent a wrong RoomEdgeHandle once membership expansion makes faceNodeGetRoomId succeed.
  if (volumeNode?.parentHandle?.type === "VOLUME_EDGE") {
    return undefined;
  }
  const roomId = faceNodeGetRoomId(faceNode, building);
  if (!roomId) {
    return undefined;
  }
  const room = building.rooms?.[roomId];
  if (!room) {
    return undefined;
  }
  const volumeNodeFaceIndex = faceNodeGetVolumeNodeFaceIndex(faceNode);
  if (volumeNodeFaceIndex === undefined) {
    return undefined;
  }
  const mappedEdgeIndex = volumeNodeFaceIndex - 2;
  if (mappedEdgeIndex < 0 || mappedEdgeIndex >= room.edgeIds.length) {
    return undefined;
  }
  const edgeIndex = room.edgeIds.length - 1 - mappedEdgeIndex;
  if (edgeIndex < 0 || edgeIndex >= room.edgeIds.length) {
    return undefined;
  }
  return { type: "EDGE", parent: { type: "ROOM", id: roomId }, shapeId: "outline", edgeId: edgeIndex };
}

export function faceNodeGetRoofId(
  faceNode: BuildingGraphFaceNode,
  building: BuildingGraphFilterBuilding,
): string | undefined {
  return volumeNodeParentHandleGetRoofId(faceNodeGetParentHandle(faceNode, building.buildingGraph));
}

export function faceNodeGetFreeformWallId(faceNode: BuildingGraphFaceNode): string | undefined {
  return faceNodeFreeformWallParentHandle(faceNode)?.freeformWallId;
}

export function faceNodeGetFreeformWallEdgeHandle(faceNode: BuildingGraphFaceNode): FreeformWallEdgeHandle | undefined {
  const parentHandle = faceNodeFreeformWallParentHandle(faceNode);
  if (!parentHandle) {
    return undefined;
  }
  return {
    type: "EDGE",
    parent: { type: "FREEFORM_WALL", id: parentHandle.freeformWallId },
    shapeId: "path",
    edgeId: parentHandle.edgeId,
  };
}

/** Level of a freeform wall face node, resolved from the parent entity. */
export function faceNodeGetFreeformWallLevelId(
  faceNode: BuildingGraphFaceNode,
  building: BuildingGraphFilterBuilding,
): string | undefined {
  const freeformWallId = faceNodeGetFreeformWallId(faceNode);
  if (!freeformWallId) {
    return undefined;
  }
  return building.doc?.freeformWalls?.[freeformWallId]?.levelId;
}

export function faceNodeGetFloorId(
  faceNode: BuildingGraphFaceNode,
  building: BuildingGraphFilterBuilding,
): string | undefined {
  const volumeNode = faceNodeGetVolumeNode(faceNode, building.buildingGraph);
  if (!volumeNode) {
    return undefined;
  }
  return volumeNodeGetFloorId(volumeNode, building);
}

export function faceNodeGetRoomClass(
  faceNode: BuildingGraphFaceNode,
  building: BuildingGraphFilterBuilding,
): RoomClass | undefined {
  const roomId = faceNodeGetRoomId(faceNode, building);
  return roomId ? roomGetRoomAttributes(roomId, building)?.roomClass : undefined;
}

export function faceNodeGetRoomEnclosure(
  faceNode: BuildingGraphFaceNode,
  building: BuildingGraphFilterBuilding,
): RoomEnclosure | undefined {
  const roomId = faceNodeGetRoomId(faceNode, building);
  return roomId ? roomGetRoomAttributes(roomId, building)?.roomEnclosure : undefined;
}

export function faceNodeGetSourceRoomId(
  faceNode: BuildingGraphFaceNode,
  building: BuildingGraphFilterBuilding,
): string | undefined {
  const roomId = faceNodeGetRoomId(faceNode, building);
  return roomId ? roomGetSourceRoomId(roomId, building) : undefined;
}

export function faceNodeGetLevelId(
  faceNode: BuildingGraphFaceNode,
  building: BuildingGraphFilterBuilding,
): string | undefined {
  const roomId = faceNodeGetRoomId(faceNode, building);
  if (roomId) {
    return roomGetLevelId(roomId, building);
  }
  return faceNodeGetFreeformWallLevelId(faceNode, building);
}

export function faceNodeGetRoomType(
  faceNode: BuildingGraphFaceNode,
  building: BuildingGraphFilterBuilding,
): RoomType | undefined {
  const roomId = faceNodeGetRoomId(faceNode, building);
  return roomId ? roomGetRoomType(roomId, building) : undefined;
}

export function roomIdToVolumeNodeMap(
  building: BuildingGraphFilterBuilding,
): ReadonlyMap<string, BuildingGraphVolumeNode> {
  const volumeNodes = new Map<string, BuildingGraphVolumeNode>();
  for (const volumeNode of Object.values(building.buildingGraph.volumeNodes)) {
    if (!volumeNode || volumeNode.parentHandle?.type !== "ROOM") {
      continue;
    }
    volumeNodes.set(volumeNode.parentHandle.roomId, volumeNode);
  }
  return volumeNodes;
}

export function volumeNodeGetRoomId(
  volumeNode: BuildingGraphVolumeNode,
  building: BuildingGraphFilterBuilding,
): string | undefined {
  for (const parentHandle of volumeNodeGetMembershipParentHandles(volumeNode, building.buildingGraph)) {
    const roomId = volumeNodeParentHandleGetRoomId(parentHandle);
    if (roomId !== undefined) {
      return roomId;
    }
  }
  return undefined;
}

export function volumeNodeGetFloorId(
  volumeNode: BuildingGraphVolumeNode,
  building: BuildingGraphFilterBuilding,
): string | undefined {
  for (const parentHandle of volumeNodeGetMembershipParentHandles(volumeNode, building.buildingGraph)) {
    const floorId = volumeNodeParentHandleGetFloorId(parentHandle);
    if (floorId !== undefined) {
      return floorId;
    }
  }
  return undefined;
}

export function volumeNodeGetRoofId(
  volumeNode: BuildingGraphVolumeNode,
  building: BuildingGraphFilterBuilding,
): string | undefined {
  for (const parentHandle of volumeNodeGetMembershipParentHandles(volumeNode, building.buildingGraph)) {
    const roofId = volumeNodeParentHandleGetRoofId(parentHandle);
    if (roofId !== undefined) {
      return roofId;
    }
  }
  return undefined;
}

export function volumeNodeGetSourceRoomId(
  volumeNode: BuildingGraphVolumeNode,
  building: BuildingGraphFilterBuilding,
): string | undefined {
  const roomId = volumeNodeGetRoomId(volumeNode, building);
  return roomId ? roomGetSourceRoomId(roomId, building) : undefined;
}

export function volumeNodeGetLevelId(
  volumeNode: BuildingGraphVolumeNode,
  building: BuildingGraphFilterBuilding,
): string | undefined {
  for (const parentHandle of volumeNodeGetMembershipParentHandles(volumeNode, building.buildingGraph)) {
    if (parentHandle.type === "ROOM" || parentHandle.type === "FLOOR" || parentHandle.type === "ROOF") {
      return parentHandle.levelId;
    }
  }
  return undefined;
}

export function volumeNodeGetRoomType(
  volumeNode: BuildingGraphVolumeNode,
  building: BuildingGraphFilterBuilding,
): RoomType | undefined {
  const roomId = volumeNodeGetRoomId(volumeNode, building);
  return roomId ? roomGetRoomType(roomId, building) : undefined;
}

export function volumeNodeGetRoomEdgeHandles(
  volumeNode: BuildingGraphVolumeNode,
  building: BuildingGraphFilterBuilding,
): ReadonlySet<RoomEdgeHandle> {
  const roomId = volumeNodeGetRoomId(volumeNode, building);
  return roomId ? roomGetRoomEdgeHandles(roomId, building) : new Set<RoomEdgeHandle>();
}

export function volumeNodeGetFaceNodeIds(volumeNode: BuildingGraphVolumeNode): ReadonlySet<string> {
  return new Set(volumeNode.faceNodeIds);
}

export function volumeNodeGetWallPlaneHashes(
  volumeNode: BuildingGraphVolumeNode,
  building: BuildingGraphFilterBuilding,
): ReadonlySet<string> {
  return volumeNodeGetPlaneHashes(volumeNode, building);
}

export function volumeNodeGetTopologyFaceIds(
  volumeNode: BuildingGraphVolumeNode,
  building: BuildingGraphFilterBuilding,
): ReadonlySet<string> {
  const topologyFaceIds = new Set<string>();
  for (const faceNodeId of volumeNode.faceNodeIds) {
    for (const topologyFaceId of faceNodeGetTopologyFaceIds(faceNodeId, building)) {
      topologyFaceIds.add(topologyFaceId);
    }
  }
  return topologyFaceIds;
}

export function roomGetVolumeNode(
  roomId: string,
  building: BuildingGraphFilterBuilding,
): BuildingGraphVolumeNode | undefined {
  return roomIdToVolumeNodeMap(building).get(roomId);
}

export function roomGetVolumeNodeId(roomId: string, building: BuildingGraphFilterBuilding): string | undefined {
  return roomGetVolumeNode(roomId, building)?.volumeNodeId;
}

export function roomGetSourceRoomId(roomId: string, building: BuildingGraphFilterBuilding): string | undefined {
  const room = building.rooms?.[roomId];
  if (!room) {
    return undefined;
  }
  return getSourceRoomId(room);
}

export function roomGetLevelId(roomId: string, building: BuildingGraphFilterBuilding): string | undefined {
  const room = building.rooms?.[roomId];
  if (room?.level?.id) {
    return room.level.id;
  }
  const volumeNode = roomGetVolumeNode(roomId, building);
  return volumeNode?.parentHandle?.type === "ROOM" ? volumeNode.parentHandle.levelId : undefined;
}

export function roomGetRoomClass(roomId: string, building: BuildingGraphFilterBuilding): RoomClass | undefined {
  return roomGetRoomAttributes(roomId, building)?.roomClass;
}

export function roomGetRoomEnclosure(roomId: string, building: BuildingGraphFilterBuilding): RoomEnclosure | undefined {
  return roomGetRoomAttributes(roomId, building)?.roomEnclosure;
}

export function roomGetRoomType(roomId: string, building: BuildingGraphFilterBuilding): RoomType | undefined {
  const roomType = building.rooms?.[roomId]?.type ?? building.doc?.rooms?.[roomId]?.type;
  if (roomType !== undefined) {
    return roomType;
  }
  const sourceRoomId = roomGetSourceRoomId(roomId, building);
  if (!sourceRoomId || sourceRoomId === roomId) {
    return undefined;
  }
  return building.rooms?.[sourceRoomId]?.type ?? building.doc?.rooms?.[sourceRoomId]?.type;
}

function roomGetRoomAttributes(roomId: string, building: BuildingGraphFilterBuilding) {
  const room = building.rooms?.[roomId] ?? building.doc?.rooms?.[roomId];
  if (room) {
    return staticRoomAttributes(room);
  }

  const sourceRoomId = roomGetSourceRoomId(roomId, building);
  if (!sourceRoomId || sourceRoomId === roomId) {
    return undefined;
  }

  const sourceRoom = building.rooms?.[sourceRoomId] ?? building.doc?.rooms?.[sourceRoomId];
  if (!sourceRoom) {
    return undefined;
  }
  return staticRoomAttributes(sourceRoom);
}

export function roomGetRoomEdgeHandles(
  roomId: string,
  building: BuildingGraphFilterBuilding,
): ReadonlySet<RoomEdgeHandle> {
  const room = building.rooms?.[roomId];
  if (!room) {
    return new Set<RoomEdgeHandle>();
  }
  return new Set(
    room.edgeIds.map(
      (_, edgeId): RoomEdgeHandle => ({
        type: "EDGE",
        parent: { type: "ROOM", id: roomId },
        shapeId: "outline",
        edgeId,
      }),
    ),
  );
}

export function roomGetFaceNodeIds(roomId: string, building: BuildingGraphFilterBuilding): ReadonlySet<string> {
  const volumeNode = roomGetVolumeNode(roomId, building);
  return new Set(volumeNode?.faceNodeIds ?? []);
}

export function roomGetWallPlaneHashes(roomId: string, building: BuildingGraphFilterBuilding): ReadonlySet<string> {
  const volumeNode = roomGetVolumeNode(roomId, building);
  if (!volumeNode) {
    return new Set<string>();
  }
  return volumeNodeGetPlaneHashes(volumeNode, building);
}

export function faceNodeGetTopologyFaceIds(
  faceNodeId: string,
  building: BuildingGraphFilterBuilding,
): ReadonlySet<string> {
  const topologyFaceIds = new Set<string>();
  for (const [topologyId, topology] of Object.entries(building.buildingGraph.faceTopologies)) {
    if (!topology || !topology.faceNodeIds.includes(faceNodeId)) {
      continue;
    }
    for (let faceIndex = 0; faceIndex < topology.planeTopology.planarGraph.faceHalfEdges.length; faceIndex++) {
      const nodeIds = topology.planarGraphFaceToFaceNodeIds[faceIndex] ?? [];
      if (nodeIds.includes(faceNodeId)) {
        topologyFaceIds.add(`${topologyId}:${faceIndex}`);
      }
    }
  }
  return topologyFaceIds;
}

export function roomGetTopologyFaceIds(roomId: string, building: BuildingGraphFilterBuilding): ReadonlySet<string> {
  const topologyFaceIds = new Set<string>();
  for (const faceNodeId of roomGetFaceNodeIds(roomId, building)) {
    for (const topologyFaceId of faceNodeGetTopologyFaceIds(faceNodeId, building)) {
      topologyFaceIds.add(topologyFaceId);
    }
  }
  return topologyFaceIds;
}

export function roomEdgeGetFaceNodeId(
  roomId: string,
  edgeIndex: number,
  building: BuildingGraphFilterBuilding,
): string | undefined {
  const room = building.rooms?.[roomId];
  const volumeNode = roomGetVolumeNode(roomId, building);
  if (!room || !volumeNode) {
    return undefined;
  }
  if (edgeIndex < 0 || edgeIndex >= room.edgeIds.length) {
    return undefined;
  }
  const mappedEdgeIndex = room.edgeIds.length - 1 - edgeIndex;
  return volumeNode.faceNodeIdByFaceIndex[2 + mappedEdgeIndex];
}

export function roomEdgeGetWallPlaneHash(
  roomId: string,
  edgeIndex: number,
  building: BuildingGraphFilterBuilding,
): string | undefined {
  const faceNodeId = roomEdgeGetFaceNodeId(roomId, edgeIndex, building);
  if (!faceNodeId) {
    return undefined;
  }
  const faceNode = building.buildingGraph.faceNodes[faceNodeId];
  if (!faceNode) {
    return undefined;
  }
  return faceNodeGetPlaneHash(faceNode, building);
}

export function roomEdgeGetTopologyFaceIds(
  roomId: string,
  edgeIndex: number,
  building: BuildingGraphFilterBuilding,
): ReadonlySet<string> {
  const faceNodeId = roomEdgeGetFaceNodeId(roomId, edgeIndex, building);
  if (!faceNodeId) {
    return new Set<string>();
  }
  return faceNodeGetTopologyFaceIds(faceNodeId, building);
}

export function planeTopologies(
  plane: BuildingGraphPlane,
  building: BuildingGraphFilterBuilding,
): BuildingGraphPlanarTopology[] {
  const topologies: BuildingGraphPlanarTopology[] = [];
  for (const topologyId of plane.planeTopologyIds) {
    const topology = building.buildingGraph.faceTopologies[topologyId];
    if (topology) {
      topologies.push(topology);
    }
  }
  return topologies;
}

export function planeNormalToZAxisAngle(
  plane: BuildingGraphPlane,
  building: BuildingGraphFilterBuilding,
): number | undefined {
  const topology = planeTopologies(plane, building)[0];
  return topology?.normalToZAxisAngle;
}

export function planeFaceNodeIds(plane: BuildingGraphPlane, building: BuildingGraphFilterBuilding): Set<string> {
  const ids = new Set<string>();
  for (const topology of planeTopologies(plane, building)) {
    for (const faceNodeId of topology.faceNodeIds) {
      ids.add(faceNodeId);
    }
  }
  return ids;
}

export function planePlanarGraphFaceCount(plane: BuildingGraphPlane, building: BuildingGraphFilterBuilding): number {
  let count = 0;
  for (const topology of planeTopologies(plane, building)) {
    count += topology.planeTopology.planarGraph.faceHalfEdges.length;
  }
  return count;
}

export function planeExteriorPlanarGraphFaceCount(
  plane: BuildingGraphPlane,
  building: BuildingGraphFilterBuilding,
): number {
  let count = 0;
  for (const topology of planeTopologies(plane, building)) {
    for (let faceIndex = 0; faceIndex < topology.planeTopology.planarGraph.faceHalfEdges.length; faceIndex++) {
      if (topology.planeTopology.planarGraph.faceWindingDirs[faceIndex] === WindingDir.CW) {
        continue;
      }
      if ((topology.planarGraphFaceToFaceEdgeIds[faceIndex]?.length ?? 0) === 0) {
        count++;
      }
    }
  }
  return count;
}

export function planeIncidentFaceEdgeIndices(
  plane: BuildingGraphPlane,
  building: BuildingGraphFilterBuilding,
): Set<number> {
  const indices = new Set<number>();
  for (const topology of planeTopologies(plane, building)) {
    for (const faceEdgeIndices of topology.planarGraphFaceToFaceEdgeIds) {
      for (const faceEdgeIndex of faceEdgeIndices ?? []) {
        indices.add(faceEdgeIndex);
      }
    }
  }
  return indices;
}

export function planarTopologyFaceRowGetTopology(
  row: PlanarTopologyFaceRow,
  building: BuildingGraphFilterBuilding,
): BuildingGraphPlanarTopology | undefined {
  return building.buildingGraph.faceTopologies[row.topologyId];
}

export type BuildingGraphFaceNodeOperandType = "ROOM" | "FLOOR" | "ROOF" | "VOLUME_EDGE" | "UNKNOWN";

export function faceNodeIdGetTypeForOperand(
  faceNodeId: string,
  building: BuildingGraphFilterBuilding,
): BuildingGraphFaceNodeOperandType {
  const types = faceNodeIdGetTypesForOperand(faceNodeId, building);
  // Prefer the volume's own parent type when present; otherwise the first membership type.
  return types[0] ?? "UNKNOWN";
}

/**
 * Operand types contributed by a face node, including dual membership for VOLUME_EDGE
 * intersections (ROOM∩FLOOR → ROOM + FLOOR + VOLUME_EDGE).
 */
export function faceNodeIdGetTypesForOperand(
  faceNodeId: string,
  building: BuildingGraphFilterBuilding,
): BuildingGraphFaceNodeOperandType[] {
  const faceNode = building.buildingGraph.faceNodes[faceNodeId];
  if (!faceNode) {
    return ["UNKNOWN"];
  }
  const volumeNode = faceNodeGetVolumeNode(faceNode, building.buildingGraph);
  if (!volumeNode?.parentHandle) {
    return ["UNKNOWN"];
  }

  const parentType = volumeNode.parentHandle.type;
  if (parentType === "ROOM" || parentType === "FLOOR" || parentType === "ROOF") {
    return [parentType];
  }
  if (parentType !== "VOLUME_EDGE") {
    return ["UNKNOWN"];
  }

  const types: BuildingGraphFaceNodeOperandType[] = ["VOLUME_EDGE"];
  for (const membershipHandle of volumeNodeGetMembershipParentHandles(volumeNode, building.buildingGraph)) {
    if (membershipHandle.type === "ROOM" || membershipHandle.type === "FLOOR" || membershipHandle.type === "ROOF") {
      if (!types.includes(membershipHandle.type)) {
        types.push(membershipHandle.type);
      }
    }
  }
  return types;
}

export function topologyFaceGetNodeIds(topology: BuildingGraphPlanarTopology | undefined, faceIndex: number): string[] {
  if (!topology) {
    return [];
  }
  const ids = topology.planarGraphFaceToFaceNodeIds[faceIndex] ?? [];
  const unique: string[] = [];
  const seen = new Set<string>();
  for (const id of ids) {
    if (seen.has(id)) {
      continue;
    }
    seen.add(id);
    unique.push(id);
  }
  return unique;
}

export function topologyFaceIsUnbounded(topology: BuildingGraphPlanarTopology | undefined, faceIndex: number): boolean {
  return topology?.planeTopology.planarGraph.faceWindingDirs[faceIndex] === WindingDir.CW;
}
